import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { isAuthEnabled } from '../components/authConfig';

const AuthContext = createContext(null);

const buildDevUser = () => ({
  username: 'local-dev',
  token: 'local-dev-token',
  role: 'ADMIN',
  client_id: process.env.REACT_APP_DEFAULT_CLIENT_ID || 'default',
  auth_disabled: true,
});

const AuthProviderWithSSO = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = sessionStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [accounts, setAccounts] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    accounts: 0,
    regions: 0,
    jobs: 0,
    log_groups: 0,
  });

  const login = (userData) => {
    setUser(userData);
    sessionStorage.setItem('user', JSON.stringify(userData));
  };

  const auth = useOidcAuth();
  const logout = () => {
    setUser(null);
    sessionStorage.clear();
    const loginUrl =
      process.env.REACT_APP_LOGIN_URL?.trim() ||
      (typeof window !== 'undefined' ? `${window.location.origin}/login` : 'https://clairai.cloud/login');
    void auth.removeUser().then(() => {
      window.location.href = loginUrl;
    }).catch(() => {
      window.location.href = loginUrl;
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        accounts,
        setAccounts,
        dashboardData,
        setDashboardData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const AuthProviderNoSSO = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = sessionStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [accounts, setAccounts] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    accounts: 0,
    regions: 0,
    jobs: 0,
    log_groups: 0,
  });

  useEffect(() => {
    if (!user) {
      const devUser = buildDevUser();
      setUser(devUser);
      sessionStorage.setItem('user', JSON.stringify(devUser));
    }
  }, [user]);

  const login = (userData) => {
    setUser(userData);
    sessionStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    const devUser = buildDevUser();
    sessionStorage.setItem('user', JSON.stringify(devUser));
    setUser(devUser);
    window.location.href = '/dashboard';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        accounts,
        setAccounts,
        dashboardData,
        setDashboardData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider = isAuthEnabled ? AuthProviderWithSSO : AuthProviderNoSSO;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
